import type { ValueOf } from '@travel-planner/shared/types';
import { pickCss } from '@/shared/lib/css-utils';
import { Text } from '@/shared/ui';
import css from './agent-panel-footer.module.scss';

const statusClass = pickCss(css, css.status);

export const FOOTER_STATUS = {
  Idle: 'idle',
  Thinking: 'thinking',
  Listening: 'listening',
  Error: 'error',
} as const;

export type AgentPanelFooterStatus = ValueOf<typeof FOOTER_STATUS>;

export type AgentPanelFooterProps = {
  status?: AgentPanelFooterStatus;
  statusLabel?: string;
};

// Enter submits the search input (when focused) and clicks the focused
// list item otherwise — labelling it "Send" reads cleanly for both. The
// old "Add" label was a leftover from the suggest era when Enter added
// a suggestion to the active itinerary day.
const HINTS: ReadonlyArray<{ keys: string; label: string }> = [
  { keys: '↑↓', label: 'Navigate' },
  { keys: '⏎', label: 'Send' },
  { keys: '⌥', label: 'Hold to talk' },
  { keys: 'Esc', label: 'Close' },
];

export const AgentPanelFooter = ({
  status = FOOTER_STATUS.Idle,
  statusLabel,
}: AgentPanelFooterProps) => (
  <footer className={css.root}>
    <ul className={css.hints}>
      {HINTS.map(({ keys, label }) => (
        <li className={css.hint} key={keys}>
          <kbd className={css.kbd}>{keys}</kbd>
          <Text as="span" size="xs" variant="muted" weight="medium">
            {label}
          </Text>
        </li>
      ))}
    </ul>
    <span aria-live="polite" className={statusClass({ is: status })}>
      {statusLabel ?? defaultStatusLabel(status)}
    </span>
  </footer>
);

const defaultStatusLabel = (status: AgentPanelFooterStatus) => {
  switch (status) {
    case FOOTER_STATUS.Thinking:
      return 'Thinking…';
    case FOOTER_STATUS.Listening:
      return 'Listening…';
    case FOOTER_STATUS.Error:
      return 'Something went wrong';
    default:
      return 'AI ready';
  }
};
